import { createSelector } from '@reduxjs/toolkit';
import { useCallback, useEffect, useMemo } from 'react';

import { api } from '~/redux/api/createApi';
import { useAppDispatch, useAppSelector } from '~/rootState';
import { RbacPermission } from '~/types/Rbac';

type Props = {
  permissions?: RbacPermission[];
};

export const usePermissions = ({ permissions = [] }: Props) => {
  const dispatch = useAppDispatch();

  // Each pair is cached independently, so the set only has to be stable by
  // value: `[a, b]` followed by `[b]` reads `b` straight from cache, and
  // `[a, b, c]` only puts `c` on the wire. Deriving the array from its own
  // serialization keeps the identity stable for callers that build it inline.
  const permissionsKey = JSON.stringify(permissions);
  const stablePermissions = useMemo(
    (): RbacPermission[] => JSON.parse(permissionsKey),
    [permissionsKey],
  );

  useEffect(() => {
    const subscriptions = stablePermissions.map((permission) =>
      dispatch(api.endpoints.checkPermission.initiate(permission)),
    );
    return () => {
      for (const subscription of subscriptions) subscription.unsubscribe();
    };
  }, [stablePermissions, dispatch]);

  const selectEntries = useMemo(
    () =>
      createSelector(
        // Build each pair's selector once: `select()` returns a memoized
        // selector, and recreating it per call would defeat that memoization
        // and re-render this hook on every unrelated store change.
        stablePermissions.map((permission) =>
          api.endpoints.checkPermission.select(permission),
        ),
        (...entries) => entries,
      ),
    [stablePermissions],
  );
  const entries = useAppSelector(selectEntries);

  const results = useMemo(() => {
    const byResource: Record<string, Record<string, boolean>> = {};
    stablePermissions.forEach(({ resourceId, resourceType }, index) => {
      const granted = entries[index]?.data?.data?.attributes?.permission;
      if (granted === undefined) return;
      byResource[resourceId] = {
        ...byResource[resourceId],
        [resourceType]: granted,
      };
    });
    return byResource;
  }, [stablePermissions, entries]);

  const checkPermission = useCallback(
    (resourceId: string, resourceType: string) =>
      results[resourceId]?.[resourceType] ?? false,
    [results],
  );

  const hasSomePermissions = useMemo(() => {
    if (!stablePermissions.length) return true;
    return stablePermissions.some(({ resourceId, resourceType }) =>
      checkPermission(resourceId, resourceType),
    );
  }, [stablePermissions, checkPermission]);

  // `PermissionsGate` redirects on "not loading and no access", so a pair that
  // has not started yet has to read as loading — never as denied.
  const isLoading = entries.some(
    (entry) => entry.isUninitialized || entry.isLoading,
  );

  return {
    hasSomePermissions,
    isLoading,
    checkPermission,
  };
};
